import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ScanLine, Boxes, Package, AlertTriangle, CheckCircle2, Trash2, Send } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

interface KitByBarcode {
  id: string;
  barcode: string;
  alias?: string | null;
  template_name?: string | null;
  is_quarantined?: number;
  current_status?: string | null;
  current_phase?: string | null;
}
interface ItemByBarcode {
  id: string;
  barcode: string;
  is_quarantined?: number;
  awaiting_passivation?: number;
  model_name?: string;
}

type FeedbackKind = 'ok' | 'warn' | 'err';
interface Feedback { kind: FeedbackKind; text: string; at: number }

export function ExpurgoPage() {
  const qc = useQueryClient();
  const [kit, setKit] = useState<KitByBarcode | null>(null);
  const [items, setItems] = useState<ItemByBarcode[]>([]);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState(false);

  const beep = useCallback((kind: FeedbackKind) => {
    try {
      const AC = (window as any).AudioContext ?? (window as any).webkitAudioContext;
      if (!AC) return;
      const ctx: AudioContext = new AC();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = kind === 'ok' ? 880 : kind === 'warn' ? 540 : 220;
      gain.gain.value = 0.08;
      osc.start();
      osc.stop(ctx.currentTime + (kind === 'err' ? 0.35 : 0.12));
    } catch { /* sem áudio */ }
  }, []);

  const flash = useCallback((kind: FeedbackKind, text: string) => {
    setFeedback({ kind, text, at: Date.now() });
    beep(kind);
  }, [beep]);

  const onScan = useCallback(async (code: string) => {
    if (busy) return;
    setBusy(true);
    try {
      if (!kit) {
        const k = await apiGet<KitByBarcode>(`/v1/kit-instances/by-barcode/${encodeURIComponent(code)}`);
        if (k.is_quarantined) {
          flash('err', `Kit ${k.barcode} está em quarentena. Encaminhar ao supervisor.`);
          return;
        }
        setKit(k);
        flash('ok', `Kit ${k.alias ?? k.template_name ?? k.barcode} carregado. Inicie a leitura dos itens.`);
        return;
      }
      if (items.some((it) => it.barcode === code)) {
        flash('warn', `Item ${code} já lido neste expurgo.`);
        return;
      }
      const it = await apiGet<ItemByBarcode>(`/v1/items/by-barcode/${encodeURIComponent(code)}`);
      if (it.is_quarantined) {
        flash('err', `Item ${it.barcode} está em quarentena.`);
        return;
      }
      setItems((arr) => [...arr, it]);
      flash('ok', `Item ${it.model_name ?? it.barcode} adicionado.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) flash('err', `Código ${code} não cadastrado.`);
      else flash('err', `Falha ao ler ${code}: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }, [busy, kit, items, flash]);

  useBarcodeScanner({ onScan, minLength: 4, enabled: true });

  const reconcile = useMutation({
    mutationFn: () => apiPost<{ kit_instance_id: string; missing: string[]; extra: string[]; alert: boolean }>(
      '/v1/lifecycle/expurgo/reconcile',
      {
        kit_instance_id: kit!.id,
        scanned_item_ids: items.map((i) => i.id),
        consumables_balance: [],
      },
      { idempotencyKey: `expurgo-${kit!.id}-${Date.now()}` },
    ),
    onSuccess: (data) => {
      if (data.alert) flash('warn', `Reconciliação concluída com divergência: ${data.missing.length} faltando.`);
      else flash('ok', 'Reconciliação concluída sem divergências.');
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setKit(null);
      setItems([]);
    },
    onError: (e) => flash('err', `Reconciliação falhou: ${(e as Error).message}`),
  });

  const resetAll = () => { setKit(null); setItems([]); setFeedback(null); };

  const feedbackTone = useMemo(() => {
    if (!feedback) return '';
    return feedback.kind === 'err'
      ? 'border-danger bg-danger-bg text-danger animate-pulse'
      : feedback.kind === 'warn'
        ? 'border-warning bg-warning-bg text-neutral-900'
        : 'border-success bg-success-bg text-success';
  }, [feedback]);

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
            Expurgo — Conferência cega
          </h1>
          <p className="text-sm text-neutral-500">
            Bipe o kit recebido e, em seguida, cada item. O sistema não mostra o esperado.
          </p>
        </div>
        <Button variant="secondary" size="lg" leadingIcon={<Trash2 size={18} />} onClick={resetAll} disabled={!kit && items.length === 0}>
          Limpar
        </Button>
      </header>

      {feedback ? (
        <div className={`rounded border-2 px-4 py-3 text-md font-medium ${feedbackTone}`} role="status" aria-live="polite">
          {feedback.text}
        </div>
      ) : (
        <div className="rounded border border-dashed border-neutral-300 px-4 py-3 text-sm text-neutral-500 dark:border-neutral-700">
          Aguardando leitura. Aproxime o scanner do código.
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Kit em conferência" subtitle="Primeiro código deve ser do kit" />
          <CardBody className="space-y-3">
            {kit ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    <Boxes size={24} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-md font-semibold">{kit.alias ?? kit.template_name ?? 'Kit'}</p>
                    <p className="font-mono text-xs text-neutral-500">{kit.barcode}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-y-1 text-xs">
                  <dt className="text-neutral-500">Status</dt>
                  <dd className="font-medium">{kit.current_status ?? '—'}</dd>
                  <dt className="text-neutral-500">Fase</dt>
                  <dd className="font-medium">{kit.current_phase ?? '—'}</dd>
                </dl>
              </>
            ) : (
              <div className="flex h-32 flex-col items-center justify-center gap-2 text-center">
                <ScanLine size={36} strokeWidth={1.5} className="text-neutral-400" />
                <p className="text-sm text-neutral-500">Bipe o kit recebido do centro cirúrgico.</p>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader
            title="Itens lidos"
            subtitle={`${items.length} item(ns) capturado(s)`}
            action={<Badge tone={items.length ? 'brand' : 'neutral'}>{items.length}</Badge>}
          />
          <CardBody className="p-0">
            <ul className="max-h-96 divide-y divide-neutral-200 overflow-y-auto dark:divide-neutral-700">
              {items.length === 0 ? (
                <li className="grid place-items-center p-10 text-sm text-neutral-500">
                  <Package size={36} strokeWidth={1.5} className="mb-2 text-neutral-400" />
                  Nenhum item lido ainda.
                </li>
              ) : items.map((it, idx) => (
                <li key={it.id} className="flex items-center justify-between gap-3 px-4 py-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="grid h-7 w-7 place-items-center rounded bg-neutral-100 text-xs font-semibold text-neutral-600 dark:bg-neutral-700 dark:text-neutral-200">{idx + 1}</span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{it.model_name ?? 'Item'}</p>
                      <p className="font-mono text-xxs text-neutral-500">{it.barcode}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="ict-focus-ring rounded px-2 py-1 text-xs text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
                    onClick={() => setItems((arr) => arr.filter((x) => x.id !== it.id))}
                  >
                    Remover
                  </button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-4 dark:border-neutral-700">
        {kit && items.length > 0 ? (
          <span className="flex items-center gap-1 text-xs text-neutral-500">
            <AlertTriangle size={14} /> Itens não bipados serão marcados como faltantes em cirurgia.
          </span>
        ) : null}
        <Button
          variant="primary"
          size="touch"
          leadingIcon={reconcile.isPending ? null : <Send size={18} />}
          loading={reconcile.isPending}
          disabled={!kit || items.length === 0}
          onClick={() => reconcile.mutate()}
        >
          {reconcile.isPending ? 'Reconciliando…' : 'Finalizar reconciliação'}
        </Button>
        {reconcile.isSuccess ? (
          <span className="flex items-center gap-1 text-success text-sm">
            <CheckCircle2 size={16} /> enviado
          </span>
        ) : null}
      </div>
    </div>
  );
}
